'use client';

/**
 * Hook for managing thread creation and retrieval
 */

import { useState, useCallback } from 'react';
import type { ThreadInfo, CreateThreadRequest, CreateThreadResponse, SupportChatConfig } from '../types/support-chat';

export function useThreadManagement(config: SupportChatConfig, bearerToken?: string) {
  const [threadInfo, setThreadInfo] = useState<ThreadInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrGetThread = useCallback(
    async (request: CreateThreadRequest): Promise<ThreadInfo | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const apiBaseUrl = config.apiBaseUrl || '/api/support';
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (bearerToken) {
          headers['Authorization'] = `Bearer ${bearerToken}`;
        }
        
        const response = await fetch(`${apiBaseUrl}/create`, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to create thread' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data: CreateThreadResponse = await response.json();
        const info: ThreadInfo = {
          threadId: data.threadId,
          channelId: data.channelId,
          createdAt: data.createdAt,
          // Carry the grant through — without it the caller cannot read or
          // write this thread afterwards.
          grant: data.grant,
        };

        setThreadInfo(info);
        return info;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create thread';
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [config, bearerToken]
  );

  return {
    threadInfo,
    isLoading,
    error,
    createOrGetThread,
  };
}

